/* Panier Ajax : tiroir latéral + page panier, API Ajax de Shopify (/cart/add.js, /cart/change.js, /cart.js) et API de rendu de sections.
   Maquette : js/cart.js (adaptateur « shopify » devenu l'unique adaptateur) + marqueurs data-cart-* de sections/h01_entete.html.

   Comportements conservés de la maquette
   - plafond de quantité par article : window.MI.cart.maxQty (réglage « Quantité maximale par article »), message cart.cap ;
   - port offert dès 30 € : barre et message rendus côté serveur (snippet mi-cart-progress, seuil = réglage du thème) ;
   - tiroir : ouverture après ajout, Échap, clic sur le voile, focus piégé puis restitué, défilement du fond bloqué ;
   - sous-total, état vide, retrait d'une ligne.

   Principes
   - Le HTML du tiroir vient du serveur (section mi-cart-drawer) : aucune chaîne de texte en dur ici, tout est dans locales/.
   - Les clés de ligne ne sont pas persistantes : après chaque action on relit le rendu serveur, jamais d'état local.
   - Sans JavaScript, les formulaires natifs (/cart/add, /cart) fonctionnent.
   - Interface publique : window.MiCart { add, change, remove, get, open, close } + évènements mi:cart (détail = panier) et mi:cart-open. */
import { $, $$, live, trapFocus } from 'mi/dom';

const MI = window.MI || {};
const ROUTES = MI.routes || {};
const CFG = MI.cart || {};
const TEXT = MI.i18n || {};
const MAX = Number(CFG.maxQty) || 10;
const REFRESH = '[data-mi-cart-refresh]';

let opener = null;          // élément qui a ouvert le tiroir (focus restitué à la fermeture)
let releaseTrap = null;
let busy = false;

/* ---------- Requêtes ---------- */
const sectionIds = () => {
  const ids = new Set();
  $$(REFRESH).forEach((el) => ids.add(el.dataset.miCartRefresh));
  return [...ids].slice(0, 5);   // l'API de rendu accepte 5 sections au maximum
};

async function post(url, body) {
  const ids = sectionIds();
  const payload = ids.length ? { ...body, sections: ids.join(','), sections_url: window.location.pathname } : body;
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
  let data = null;
  try { data = await res.json(); } catch (e) { /* réponse vide */ }
  if (!res.ok) {
    const err = new Error((data && (data.description || data.message)) || TEXT.error || 'cart');
    err.status = res.status; err.data = data;
    throw err;
  }
  return data;
}

export async function get() {
  const res = await fetch(ROUTES.cartJs || '/cart.js', { headers: { Accept: 'application/json' } });
  return res.json();
}

/* ---------- Rendu ---------- */
function paint(sections) {
  if (!sections) return;
  const active = document.activeElement;
  const lineKey = active && active.closest && active.closest('.cart-line, .pn-line') ? active.closest('.cart-line, .pn-line').dataset.key : null;
  const action = ['data-cart-inc', 'data-cart-dec', 'data-cart-rm'].find((a) => active && active.hasAttribute && active.hasAttribute(a));
  for (const [id, html] of Object.entries(sections)) {
    const target = $(`[data-mi-cart-refresh="${id}"]`);
    if (!target || !html) continue;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(`[data-mi-cart-refresh="${id}"]`);
    if (fresh) target.innerHTML = fresh.innerHTML;
  }
  // focus : on revient sur la même commande de la même ligne ; sinon sur le premier élément utile du tiroir
  if (lineKey && action) {
    const next = $(`:is(.cart-line, .pn-line)[data-key="${CSS.escape(lineKey)}"] [${action}]`) || $('[data-cart-close]');
    if (next) next.focus();
  } else if (drawerOpen() && !(drawer().contains(document.activeElement))) {
    const close = $('[data-cart-close]'); if (close) close.focus();
  }
}

function setCount(cart) {
  $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
}

function setCap(message) {
  $$('[data-cart-cap]').forEach((el) => { el.textContent = message || ''; });
  if (message) live(message);
}

async function refresh(sections, { cartData } = {}) {
  paint(sections);
  const cart = cartData || await get();
  setCount(cart);
  document.dispatchEvent(new CustomEvent('mi:cart', { detail: cart }));
  return cart;
}

/* ---------- Actions ---------- */
export async function add(variantId, quantity = 1, properties) {
  if (busy) return null;
  busy = true;
  try {
    const before = await get();
    const inCart = before.items.filter((i) => String(i.variant_id) === String(variantId)).reduce((n, i) => n + i.quantity, 0);
    const room = Math.max(0, MAX - inCart);
    if (room === 0) { setCap(TEXT.cap); open(); return before; }
    const qty = Math.min(Number(quantity) || 1, room);
    const item = { id: Number(variantId), quantity: qty };
    if (properties) item.properties = properties;
    const data = await post(ROUTES.cartAdd || '/cart/add.js', { items: [item] });
    const cart = await refresh(data.sections);
    const capped = qty < Number(quantity) || cart.items.some((i) => i.quantity >= MAX);
    setCap(capped ? TEXT.cap : '');
    if (!drawer() || CFG.type === 'page') {
      if (!$('[data-mi-cart-refresh]')) window.location.href = ROUTES.cart || '/cart';
    } else {
      open();
    }
    confirmAdded();
    document.dispatchEvent(new CustomEvent('mi:cart-open'));
    return cart;
  } finally { busy = false; }
}

/* Confirmation en tête du tiroir après un ajout (« Ajouté au panier ») ; effacée à la fermeture et à toute autre action */
function confirmAdded() {
  $$('[data-cart-added]').forEach((el) => {
    const text = $('[data-cart-added-text]', el); if (text) text.textContent = TEXT.added || '';
    el.hidden = !TEXT.added;
  });
  if (TEXT.added) live(TEXT.added);
}
function clearAdded() { $$('[data-cart-added]').forEach((el) => { el.hidden = true; }); }

export async function change(key, quantity) {
  if (busy) return null;
  busy = true;
  try {
    const q = Math.max(0, Math.min(MAX, Number(quantity)));
    const data = await post(ROUTES.cartChange || '/cart/change.js', { id: key, quantity: q });
    const cart = await refresh(data.sections, { cartData: data });
    setCap(Number(quantity) > MAX ? TEXT.cap : (cart.items.some((i) => i.quantity >= MAX) ? TEXT.cap : ''));
    return cart;
  } finally { busy = false; }
}
export const remove = (key) => change(key, 0);

/* ---------- Tiroir ---------- */
const drawer = () => document.getElementById('mi-cart-drawer');
const drawerOpen = () => !!(drawer() && drawer().classList.contains('is-open'));

export function open(trigger) {
  const d = drawer(); if (!d) return;
  if (trigger) opener = trigger; else if (!opener) opener = document.activeElement;
  d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false');
  document.body.classList.add('no-scroll');
  $$('[data-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
  releaseTrap = trapFocus(d);
  const close = $('[data-cart-close]', d); if (close) close.focus();
}

export function close() {
  const d = drawer(); if (!d || !d.classList.contains('is-open')) return;
  clearAdded();
  d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('no-scroll');
  $$('[data-cart-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  if (releaseTrap) { releaseTrap(); releaseTrap = null; }
  if (opener && opener.focus) opener.focus();
  opener = null;
}

/* ---------- Évènements ---------- */
function onClick(e) {
  const addBtn = e.target.closest('[data-add-to-cart]');
  if (addBtn) {
    e.preventDefault();
    addBtn.setAttribute('aria-busy', 'true');
    add(addBtn.dataset.addToCart, Number(addBtn.dataset.qty || 1), addBtn.dataset.propName ? { [addBtn.dataset.propName]: addBtn.dataset.propValue || '' } : undefined).catch((err) => setCap(err.message || TEXT.error)).finally(() => addBtn.removeAttribute('aria-busy'));
    return;
  }
  const openBtn = e.target.closest('[data-cart-open]');
  if (openBtn && drawer() && CFG.type !== 'page') { e.preventDefault(); open(openBtn); return; }
  if (e.target.closest('[data-cart-close]') || e.target.matches('[data-cart-overlay]')) { close(); return; }
  const line = e.target.closest('.cart-line, .pn-line');
  if (!line) return;
  const key = line.dataset.key;
  const qty = Number((line.querySelector('output') || {}).textContent) || 1;
  if (e.target.closest('[data-cart-inc]')) change(key, qty + 1).catch((err) => setCap(err.message || TEXT.error));
  else if (e.target.closest('[data-cart-dec]')) change(key, qty - 1).catch((err) => setCap(err.message || TEXT.error));
  else if (e.target.closest('[data-cart-rm]')) remove(key).catch((err) => setCap(err.message || TEXT.error));
}

/* Formulaires produit : ajout Ajax ; en cas d'échec, message dans le formulaire (jamais d'alerte navigateur) */
async function onSubmit(e) {
  const form = e.target.closest('form[data-mi-product-form]');
  if (!form) return;
  e.preventDefault();
  const data = new FormData(form);
  const id = data.get('id'); const qty = Number(data.get('quantity') || 1);
  const props = {}; let hasProps = false;
  data.forEach((v, k) => { const m = /^properties\[(.+)\]$/.exec(k); if (m && v) { props[m[1]] = v; hasProps = true; } });
  const btn = form.querySelector('[data-mi-add]'); const error = form.querySelector('[data-mi-error]');
  if (error) { error.hidden = true; error.textContent = ''; }
  if (btn) { btn.setAttribute('aria-busy', 'true'); btn.setAttribute('aria-disabled', 'true'); }
  try {
    await add(id, qty, hasProps ? props : undefined);
  } catch (err) {
    if (error) { error.textContent = err.message || TEXT.error || ''; error.hidden = false; }
  } finally {
    if (btn) { btn.removeAttribute('aria-busy'); btn.removeAttribute('aria-disabled'); }
  }
}

export function initCart() {
  if (initCart.done) return; initCart.done = true;
  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmit);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  document.addEventListener('mi:cart-add', (e) => { const d = e.detail || {}; add(d.id, d.quantity || 1, d.properties).catch((err) => setCap(err.message || TEXT.error)); });
  // retour arrière / bfcache : le compteur doit refléter le panier réel
  window.addEventListener('pageshow', (e) => { if (e.persisted) get().then(setCount).catch(() => {}); });
}

/* Filet de sécurité : un article à 0 € (Tome 2, pack, merchandising « Bientôt ») ne doit jamais rester au panier, même ajouté par lien direct (le paiement le refuse déjà : stock-problem). */
let purging = false;
async function purge(cart) {
  if (purging) return; const bad = ((cart && cart.items) || []).filter((i) => i.price === 0 && i.final_line_price === 0);
  if (!bad.length) return; purging = true;
  try { for (const i of bad) await change(i.key, 0); } catch (e) { /* silencieux */ } finally { purging = false; }
}
document.addEventListener('mi:cart', (e) => { purge(e.detail); });
get().then(purge).catch(() => {});

window.MiCart = { add, change, remove, get, open, close };
initCart();
